import clsx from "clsx";
import { Settings, RefreshCw } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import type {
  UserPreferences,
  UpdateUserPreferencesInput,
  WebAnswerReadyNotificationsPreference,
} from "@/lib/types";

import { useModels } from "@/hooks/useModels";
import { usePreferences, useUpdatePreferences } from "@/hooks/usePreferences";
import { useStore } from "@/lib/store";

import { getModelLabel } from "./stats/formatters";

type PreferencesDraft = {
  defaultMode: "build" | "plan";
  defaultModelId: string | null;
  defaultPersona: string;
  webAnswerReadyNotifications: WebAnswerReadyNotificationsPreference;
};

const EMPTY_DRAFT: PreferencesDraft = {
  defaultMode: "build",
  defaultModelId: null,
  defaultPersona: "",
  webAnswerReadyNotifications: "not_asked",
};

function toDraft(preferences: UserPreferences): PreferencesDraft {
  return {
    defaultMode: preferences.defaultMode ?? "build",
    defaultModelId: preferences.defaultModelId,
    defaultPersona: preferences.defaultPersona ?? "",
    webAnswerReadyNotifications: preferences.webAnswerReadyNotifications,
  };
}

function areDraftsEqual(a: PreferencesDraft, b: PreferencesDraft): boolean {
  return (
    a.defaultMode === b.defaultMode &&
    a.defaultModelId === b.defaultModelId &&
    a.defaultPersona === b.defaultPersona &&
    a.webAnswerReadyNotifications === b.webAnswerReadyNotifications
  );
}

async function maybeRequestNotificationPermission(
  preference: WebAnswerReadyNotificationsPreference,
): Promise<"granted" | "denied" | "default" | "unsupported"> {
  if (preference !== "enabled") {
    return "granted";
  }

  if (typeof window === "undefined" || typeof Notification === "undefined") {
    return "unsupported";
  }

  if (Notification.permission === "granted") {
    return "granted";
  }

  return Notification.requestPermission();
}

export function PreferencesView(): JSX.Element {
  const showToast = useStore((state) => state.showToast);
  const setComposerMode = useStore((state) => state.setComposerMode);
  const setComposerModelId = useStore((state) => state.setComposerModelId);
  const { data: preferencesData, isLoading: isLoadingPreferences } = usePreferences();
  const { data: modelsData, isLoading: isLoadingModels } = useModels();
  const updatePreferences = useUpdatePreferences();

  const [draft, setDraft] = useState<PreferencesDraft>(EMPTY_DRAFT);
  const [savedDraft, setSavedDraft] = useState<PreferencesDraft>(EMPTY_DRAFT);
  const hasHydratedRef = useRef(false);

  useEffect(() => {
    if (!preferencesData?.preferences || hasHydratedRef.current) {
      return;
    }

    const hydrated = toDraft(preferencesData.preferences);
    setDraft(hydrated);
    setSavedDraft(hydrated);
    hasHydratedRef.current = true;
  }, [preferencesData?.preferences]);

  const isDirty = useMemo(() => !areDraftsEqual(draft, savedDraft), [draft, savedDraft]);

  function handleReset(): void {
    if (!preferencesData?.preferences) {
      return;
    }

    const reset = toDraft(preferencesData.preferences);
    setDraft(reset);
    setSavedDraft(reset);
  }

  function handleSave(): void {
    const payload: UpdateUserPreferencesInput = {
      defaultMode: draft.defaultMode,
      defaultModelId: draft.defaultModelId,
      defaultPersona: draft.defaultPersona.trim() ? draft.defaultPersona.trim() : null,
      webAnswerReadyNotifications: draft.webAnswerReadyNotifications,
    };

    updatePreferences.mutate(payload, {
      onSuccess: async (result) => {
        const next = toDraft(result.preferences);
        setDraft(next);
        setSavedDraft(next);
        setComposerMode(next.defaultMode);
        setComposerModelId(next.defaultModelId);
        showToast("Preferences saved", "success");

        const permission = await maybeRequestNotificationPermission(
          next.webAnswerReadyNotifications,
        );
        if (permission === "denied") {
          showToast("Browser notifications are blocked. Enable them in browser settings.", "error");
        } else if (permission === "unsupported") {
          showToast("Browser notifications are not supported in this browser.", "error");
        }
      },
      onError: (error) => {
        const message = error instanceof Error ? error.message : "Failed to save preferences";
        showToast(message, "error");
      },
    });
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex-shrink-0 border-b border-cyber-border px-4 py-3">
        <div className="flex items-center justify-between px-6">
          <div className="flex items-center gap-2">
            <Settings size={16} className="text-cyber-cyan" />
            <h2 className="text-sm font-medium text-cyber-text">Preferences</h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleReset}
              disabled={!isDirty || updatePreferences.isPending}
              className="rounded border border-cyber-border px-3 py-1.5 text-xs text-cyber-muted transition-all hover:bg-cyber-surface/50 hover:text-cyber-text disabled:cursor-not-allowed disabled:opacity-50"
            >
              Reset
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!isDirty || updatePreferences.isPending}
              className="rounded border border-cyber-cyan/30 px-3 py-1.5 text-xs text-cyber-cyan transition-all hover:bg-cyber-cyan/10 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {updatePreferences.isPending ? "Saving..." : "Save changes"}
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        <div className="mx-auto max-w-3xl space-y-4">
          {isLoadingPreferences ? (
            <div className="py-8 text-center text-cyber-muted">
              <span className="animate-spin-glow mr-2 inline-block">◐</span>
              Loading preferences...
            </div>
          ) : (
            <>
              <section className="rounded border border-cyber-border bg-cyber-bg p-4">
                <h3 className="mb-1 text-sm text-cyber-text">Default mode</h3>
                <p className="mb-3 text-xs text-cyber-muted">
                  Used when starting a new chat from the home composer.
                </p>
                <div className="flex gap-2">
                  {(["build", "plan"] as const).map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => setDraft((current) => ({ ...current, defaultMode: mode }))}
                      className={clsx(
                        "rounded border px-3 py-1.5 text-xs uppercase tracking-wide transition-all",
                        draft.defaultMode === mode
                          ? "border-cyber-cyan/40 bg-cyber-cyan/10 text-cyber-cyan"
                          : "border-cyber-border text-cyber-muted hover:bg-cyber-surface/50 hover:text-cyber-text",
                      )}
                    >
                      {mode}
                    </button>
                  ))}
                </div>
              </section>

              <section className="rounded border border-cyber-border bg-cyber-bg p-4">
                <h3 className="mb-1 text-sm text-cyber-text">Default model</h3>
                <p className="mb-3 text-xs text-cyber-muted">
                  Pre-selected for new chats. You can still override in the composer.
                </p>
                <div className="relative">
                  <select
                    value={draft.defaultModelId ?? ""}
                    onChange={(e) =>
                      setDraft((current) => ({
                        ...current,
                        defaultModelId: e.target.value.trim() ? e.target.value : null,
                      }))
                    }
                    className="w-full rounded border border-cyber-border bg-cyber-surface px-3 py-2 text-sm text-cyber-text focus:border-cyber-cyan/50 focus:outline-none"
                    disabled={isLoadingModels || !modelsData}
                  >
                    <option value="">OpenCode default</option>
                    {modelsData?.models.map((model) => (
                      <option key={model.fullId} value={model.fullId}>
                        {`${model.providerName} · ${getModelLabel(model.providerId, model.modelId, model.modelName)}`}
                      </option>
                    ))}
                  </select>
                  {isLoadingModels && (
                    <span className="pointer-events-none absolute right-3 top-2.5 text-cyber-muted">
                      <RefreshCw size={14} className="animate-spin" />
                    </span>
                  )}
                </div>
              </section>

              <section className="rounded border border-cyber-border bg-cyber-bg p-4">
                <h3 className="mb-1 text-sm text-cyber-text">Default persona</h3>
                <p className="mb-3 text-xs text-cyber-muted">
                  Applied only to the first message in a new thread.
                </p>
                <textarea
                  value={draft.defaultPersona}
                  onChange={(e) =>
                    setDraft((current) => ({
                      ...current,
                      defaultPersona: e.target.value.slice(0, 4000),
                    }))
                  }
                  rows={6}
                  placeholder="Example: Be concise, include likely root causes first, and end with next actions."
                  className="w-full resize-y rounded border border-cyber-border bg-cyber-surface px-3 py-2 text-sm text-cyber-text placeholder-cyber-muted focus:border-cyber-cyan/50 focus:outline-none"
                />
                <p className="mt-1 text-right text-[11px] text-cyber-muted">
                  {draft.defaultPersona.length}/4000
                </p>
              </section>

              <section className="rounded border border-cyber-border bg-cyber-bg p-4">
                <h3 className="mb-1 text-sm text-cyber-text">Answer-ready notifications</h3>
                <p className="mb-3 text-xs text-cyber-muted">
                  Controls browser notifications when a response is ready.
                </p>
                <select
                  value={draft.webAnswerReadyNotifications}
                  onChange={(e) =>
                    setDraft((current) => ({
                      ...current,
                      webAnswerReadyNotifications: e.target
                        .value as WebAnswerReadyNotificationsPreference,
                    }))
                  }
                  className="w-full rounded border border-cyber-border bg-cyber-surface px-3 py-2 text-sm text-cyber-text focus:border-cyber-cyan/50 focus:outline-none"
                >
                  <option value="not_asked">Ask me later</option>
                  <option value="enabled">Enabled</option>
                  <option value="disabled">Disabled</option>
                </select>
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
